import {
  Component,
  OnInit,
  Inject,
  Output,
  EventEmitter,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  ViewEncapsulation,
  PLATFORM_ID,
  ErrorHandler,
  ViewChild,
  ElementRef,
  AfterViewInit,
} from '@angular/core';
import {
  MatBottomSheetRef,
  MAT_BOTTOM_SHEET_DATA,
} from '@angular/material/bottom-sheet';
import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { Constants } from '@app/core/models/constants';
import { SiteService } from '@app/core/services/site/site.service';
import { SearchSiteInput } from '@app/core/models/search-site-input.model';
import { Observable, of } from 'rxjs';
import {
  catchError,
  debounceTime,
  distinctUntilChanged,
  finalize,
  first,
  switchMap,
  tap,
} from 'rxjs/operators';
import { SiteModel } from '@app/core/models/site.model';
import { LocationService } from '@app/core/location/location.service';
import { SiteSearchSelectionModel } from '../../models/site-search-selection.model';
import { BackBottomSheet } from '@app/shared/classes/back-bottom-sheet';
import { Router } from '@angular/router';
import { SiteSearchInputModel } from '../../models/site-search-input.model';
import _groupBy from 'lodash-es/groupBy';
import _keys from 'lodash-es/keys';
@Component({
    selector: 'app-site-search-list',
    templateUrl: './site-search-list.component.html',
    styleUrls: ['./site-search-list.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    standalone: false
})
export class SiteSearchListComponent extends BackBottomSheet<SiteSearchListComponent> implements OnInit, AfterViewInit {
  @Output() selection = new EventEmitter<SiteSearchSelectionModel>();

  sites: SiteModel[];
  canSearch: boolean;
  canClear: boolean;
  searchForm: UntypedFormGroup;

  isLoading = false;
  searchNoResult = false;

  @ViewChild('searchValue') searchValue: ElementRef;

  constructor(
    bottomSheetRef: MatBottomSheetRef<SiteSearchListComponent>,
    @Inject(MAT_BOTTOM_SHEET_DATA) public data: SiteSearchInputModel,
    @Inject(PLATFORM_ID) platformId: object,
    private _formBuilder: UntypedFormBuilder,
    private _siteService: SiteService,
    private _locationService: LocationService,
    private _cd: ChangeDetectorRef,
    private _errorHandler: ErrorHandler,
    router: Router
  ) {
    super(bottomSheetRef, router, platformId);
  }


  override ngOnInit(): void {
    super.ngOnInit();
    this.removeStateOnDestroy = true;
    this.initFormAndData();
  }

  ngAfterViewInit(): void {
    // apply searchTerm if defined
    if (!this.data.byLocation && this.data.searchTerm) {
      this.searchForm.controls.searchValue.setValue(this.data.searchTerm);
      // Select whole text
      (this.searchValue.nativeElement as HTMLInputElement).select();
    }
  }

  private initFormAndData() {
    this.createForm();
    this.isLoading = false;
    this.searchNoResult = false;
  }

  private createForm() {
    if (this.data.byLocation) {
      this._locationService.getPosition().then((location) => {

        if (location == null) {
          this.sites = [];
          this.searchNoResult = true;
          this._cd.markForCheck();
          return;
        }

        this.showLoading();
        this.searchSitesByLocation(location.latitude, location.longitude)
          .pipe(
            finalize(() => {
              this.hideLoading();
            })
          )
          .subscribe((sites) => {
            this.sites = this.applySearchModeFilter(sites);
          });
      });
    }

    this.searchForm = this._formBuilder.group({
      searchValue: [''],
    });

    this.searchForm.controls.searchValue.valueChanges
      .pipe(
        debounceTime(Constants.searchDebounceTimeInMs),
        distinctUntilChanged(),
        switchMap((searchValue) => this.launchSearchByText(searchValue)
        )
      )
      .subscribe(sites => {
        this.sites = this.applySearchModeFilter(sites);
        this._cd.markForCheck();
      });
  }

  launchSearchByText(searchValue: string): Observable<SiteModel[]> {
    this.checkCanSearch(searchValue);
    this.checkCanClear(searchValue);

    this.searchNoResult = false;

    if (this.canSearch) {
      this.showLoading();

      return this.searchSitesByText(searchValue)
        .pipe(
          finalize(() => {
            this.hideLoading();
          })
        );
    } else {
      return of([]);
    }
  }

  private applySearchModeFilter(sites: SiteModel[]): SiteModel[] {
    if (this.data.searchMode === 'site') {
      // site is repeated for each matching restaurant, group by siteId and display only first result
      const sitesById = _groupBy(sites, 'siteId');
      return _keys(sitesById).map(siteId => sitesById[siteId][0]);
    }
    // else display each result (site & matching restaurant)
    return sites;
  }

  private checkCanSearch(searchValue: string) {
    this.canSearch = searchValue && searchValue.length >= Constants.minSearchLength;
  }

  private checkCanClear(searchValue: string) {
    this.canClear = searchValue && searchValue.length >= 0;
  }

  private showLoading() {
    this.isLoading = true;
    this._cd.markForCheck();
  }

  private hideLoading() {
    this.isLoading = false;
    this._cd.markForCheck();
  }

  private searchSitesByText(searchValue: string): Observable<SiteModel[]> {
    const searchInput: SearchSiteInput = this.data.byLocation
      ? { lat: this.data.latitude, lng: this.data.longitude }
      : { searchTerm: searchValue };

    return this.searchSites(searchInput);
  }

  private searchSitesByLocation(
    lat: number,
    lng: number
  ): Observable<SiteModel[]> {
    const searchInput: SearchSiteInput = { lat, lng };

    return this.searchSites(searchInput);
  }

  private searchSites(searchInput: SearchSiteInput): Observable<SiteModel[]> {
    return this._siteService
      .search$(searchInput)
      .pipe(
        first(),
        // catch errors in inner observal pipeline to prevent higher-order observal to fail => see: https://stackoverflow.com/a/58661330/590741
        catchError((error: unknown) => {
          // notify error but continue observal
          this._errorHandler.handleError(error);
          return of([]);
        }),
        tap((sites) => (this.searchNoResult = sites?.length === 0))
      );
  }

  onCancelClick(event: MouseEvent): void {
    this.closeBottomSheet();
    event.preventDefault();
  }

  onClearSearchValue() {
    this.searchForm.reset();
    // this.onSearchChange('');
  }

  onSelectSite(site: SiteModel) {
    this.selection.emit({ site, byLocation: this.data.byLocation, } as SiteSearchSelectionModel);
    this.closeBottomSheet();
  }

  private closeBottomSheet() {
    this.bottomSheetRef.dismiss(true);
  }

  onSearchRetry(byLocation: boolean) {
    this.data.byLocation = byLocation;
    this.initFormAndData();
  }
}
